/**
 * Apply persisted filter JSON from unified_task_saved_views to execution-board UI state.
 * Keys must stay aligned with backend SAVED_VIEW_FILTER_KEYS.
 */
export interface ApplySavedViewFilterContext {
  setTaskModuleFilter: (v: string | undefined) => void;
  setStatusFilter: (v: string | undefined) => void;
  setPriorityFilter?: (v: string | undefined) => void;
  setTaskTypeFilter: (v: string | undefined) => void;
  setSourceDocTypeFilter: (v: string | undefined) => void;
  setTaskOriginFilter: (v: 'all' | 'system' | 'manual') => void;
  setTaskSearchQ: (q: string) => void;
  setTaskSearchDebounced: (q: string) => void;
  setOverdueOnly?: (v: boolean) => void;
  setDueTodayOnly: (v: boolean) => void;
  setNoDueDateOnly: (v: boolean) => void;
  setOpenTasksOnly: (v: boolean) => void;
  /** Enterprise & team board: me / unassigned / team. Omit on “My tasks” page. */
  assignment?: {
    setAssignedToMe: (v: boolean) => void;
    setUnassignedOnly: (v: boolean) => void;
    setTeamOthersOnly: (v: boolean) => void;
  };
  setBlockedOnly?: (v: boolean) => void;
  setReadyToStart?: (v: boolean) => void;
  setSlaRiskFilter?: (v: 'all' | 'safe' | 'warning' | 'breach' | 'critical' | 'none') => void;
  setSortBySla?: (v: boolean) => void;
}

export function applySavedViewFilterRecord(f: Record<string, unknown>, ctx: ApplySavedViewFilterContext): void {
  const str = (k: string) => (typeof f[k] === 'string' ? (f[k] as string) : '');
  ctx.setTaskModuleFilter(str('module') || undefined);
  ctx.setStatusFilter(str('status') || undefined);
  if (ctx.setPriorityFilter) ctx.setPriorityFilter(str('priority') || undefined);
  ctx.setTaskTypeFilter(str('task_type') || undefined);
  ctx.setSourceDocTypeFilter(str('sdoc') || undefined);
  const o = f.origin;
  ctx.setTaskOriginFilter(o === 'manual' || o === 'system' ? o : 'all');
  const tq = typeof f.tq === 'string' ? f.tq : '';
  ctx.setTaskSearchQ(tq);
  ctx.setTaskSearchDebounced(tq);
  if (ctx.setOverdueOnly) ctx.setOverdueOnly(f.overdue === true);
  ctx.setDueTodayOnly(f.due_today === true);
  ctx.setNoDueDateOnly(f.nodue === true);
  ctx.setOpenTasksOnly(f.open === true);
  if (ctx.assignment) {
    const me = f.me === true;
    const un = f.unassigned === true;
    const tm = f.team === true;
    ctx.assignment.setAssignedToMe(me && !un && !tm);
    ctx.assignment.setUnassignedOnly(un && !me && !tm);
    ctx.assignment.setTeamOthersOnly(tm && !me && !un);
  }
  if (ctx.setBlockedOnly) ctx.setBlockedOnly(f.blocked === true);
  if (ctx.setReadyToStart) ctx.setReadyToStart(f.ready === true);
  if (ctx.setSlaRiskFilter) {
    const s = typeof f.sla === 'string' ? f.sla.trim().toLowerCase() : '';
    if (s === 'safe' || s === 'warning' || s === 'breach' || s === 'critical' || s === 'none') {
      ctx.setSlaRiskFilter(s);
    } else {
      ctx.setSlaRiskFilter('all');
    }
  }
  if (ctx.setSortBySla) ctx.setSortBySla(f.sla_sort === true);
}
